import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { Button } from "@accredo/fe-shared/theme/components";
import { useClaimActions } from "../../hooks/useClaimActions.jsx";
import { COVERAGE_TYPES_FIELD } from "../Form/FormDefinitionRenderer/coverageSelection.js";
import {
  selectedCoverageKeys,
  validateCoverageSteps,
} from "../Form/FormDefinitionRenderer/validateCoverageSteps.js";

/**
 * The Sparten of the stored claim in definition order, each with whether its
 * step and the shared steps are complete.
 */
function sparteEntries(claim, formDefinition) {
  const formdata =
    typeof claim.formdata === "string"
      ? JSON.parse(claim.formdata || "{}")
      : (claim.formdata ?? {});
  const ids = formdata[COVERAGE_TYPES_FIELD] ?? [];
  const objekt_sparten = claim.objekt_sparten ?? [];
  const keys = (formDefinition.coverage_types ?? []).map((type) => type.key);
  const objekt_sparte_of = (id) =>
    objekt_sparten.find((sparte) => sparte.sparte_id === id);
  const key_of = (id) => objekt_sparte_of(id)?.coverage_type;

  const validity = validateCoverageSteps(
    formDefinition,
    formdata,
    selectedCoverageKeys(ids, objekt_sparten)
  );

  return ids
    .map((id) => ({
      id,
      name: objekt_sparte_of(id)?.sparte,
      valid: Boolean(validity.shared && validity.coverage[key_of(id)]),
    }))
    .sort((a, b) => keys.indexOf(key_of(a.id)) - keys.indexOf(key_of(b.id)));
}

/**
 * Asks which Sparten of a claim are ready for review and splits the claim
 * accordingly. Incomplete Sparten cannot be ticked; they stay behind as claims
 * of their own.
 *
 * @param {Object} claim the stored claim as returned by `GET data/schaden/{id}`
 * @param {Object} formDefinition the claim form definition of the claim
 * @param {Function} onSplit called with the split response once the claim is split
 */
export default function CoverageSplitDialog({
  open,
  claim,
  formDefinition,
  onClose,
  onSplit,
}) {
  const { splitClaim } = useClaimActions();
  const [isSaving, setIsSaving] = useState(false);
  const [ticked, setTicked] = useState([]);

  const entries = useMemo(
    () => (open && claim ? sparteEntries(claim, formDefinition) : []),
    [open, claim, formDefinition]
  );

  useEffect(() => {
    setTicked(entries.filter((entry) => entry.valid).map((entry) => entry.id));
  }, [entries]);

  const toggle = (id) =>
    setTicked((current) =>
      current.includes(id)
        ? current.filter((ticked_id) => ticked_id !== id)
        : [...current, id]
    );

  const report = async () => {
    const result = await splitClaim(
      claim.id,
      entries.map((entry) => entry.id).filter((id) => ticked.includes(id)),
      { setIsLoading: setIsSaving }
    );
    if (result) onSplit(result);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Zur Prüfung melden — {claim?.schaden_nummer_accredo}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Der Schaden wird je Sparte in einen eigenen Schaden mit eigener
          Schadennummer aufgeteilt.
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Welche Sparten sollen zur Prüfung gemeldet werden?
        </Typography>
        <FormGroup>
          {entries.map((entry) => (
            <FormControlLabel
              key={entry.id}
              disabled={!entry.valid}
              control={
                <Checkbox
                  checked={ticked.includes(entry.id)}
                  onChange={() => toggle(entry.id)}
                />
              }
              label={
                <>
                  {entry.name}
                  {!entry.valid && (
                    <Chip size="small" label="unvollständig" sx={{ ml: 1 }} />
                  )}
                </>
              }
            />
          ))}
        </FormGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Nicht ausgewählte Sparten bleiben gespeichert und können später
          gemeldet werden.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="tertiary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={report}
          disabled={ticked.length === 0 || isSaving}
        >
          Melden
        </Button>
      </DialogActions>
    </Dialog>
  );
}
